import {
  Alert,
  Box,
  Button,
  CardContent,
  Card,
  CircularProgress,
  FormControl,
  Grid,
  Link,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
  Chip,
  Stack,
  LinearProgress,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import { SelecFilterChip } from 'src/App/Dashboards/Market/Components/Chips';
import ActiveSubscriptionCard from 'src/App/Dashboards/Market/Pages/Features/SettingsV2/ActiveSubscriptionCard';
import {
  BACKGROUND_COLOR_1,
  BACKGROUND_COLOR_2,
  COLOR_ERROR,
  COLOR_ERROR_BACKGROUNG,
  COLOR_GREY_200,
  COLOR_SUCCESS_1,
  COLOR_SUCCESS_BACKGROUNG,
  COLOR_TERTIARY_1_500,
  COLOR_TEXT_2,
  SIDEBAR_COLOR,
} from 'src/utils/colors';
import { callApi } from 'src/core/callApi';
import {
  clearRateLimits,
  getPlanDurationFrequency,
  getPlanLabelFromPriceID,
  getUserSubscriptionDetails,
  getUserSubscriptionHistory,
} from './helper';
import { formatDate } from 'src/utils/functions';
import { capitalize } from 'lodash';
import RefreshIcon from '@mui/icons-material/Refresh';

const User = ({ adminPassword, dataType, showSearchbar, setShowSearchbar }) => {
  const [userEmailId, setUserEmailId] = useState<any>('');
  const [userDetails, setUserDetails] = useState<any>();
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [subscribedInsightRegionRows, setSubscribedInsightRegionRows] = useState<any>();
  const [searchedInsightsRegionRows, setSearchedInsightRegionRows] = useState<any>();
  const [selectedDashboard, setSelectedDashboard] = useState<string>('insights');
  const [estimatorReportRows, setEstimatorReportRows] = useState<any>();
  const [openAddCreditsModal, setOpenAddCreditsModal] = useState<boolean>(false);
  const [openRemoveAppAccessModal, setOpenRemoveAppAccessModal] = useState<boolean>(false);
  const [showUserNotFoundAlert, setShowUserNotFoundAlert] = useState<boolean>(false);
  const [showNotAdminUserAlert, setShowNotAdminUserAlert] = useState<boolean>(false);
  const [openDeleteUserModal, setOpenDeleteUserModal] = useState<boolean>(false);
  const [userCurrentSubscriptionDetails, setUserCurrentSubscriptionDetails] = useState<any>(null);
  const [userCurrentSubscriptionDetailsError, setUserCurrentSubscriptionDetailsError] = useState<
    any
  >(null);
  const [userSubscriptionHistory, setUserSubscriptionHistory] = useState<any>([]);
  const [userSubscriptionHistoryError, setUserSubscriptionHistoryError] = useState<any>(null);
  const [clearRateLimitProcessing, setClearRateLimitProcessing] = useState<boolean>(false);
  const [revokeAppSubscriptionProcessing, setRevokeAppSubscriptionProcessing] = useState<boolean>(false);

  const subscribedInsightsRegionColumn: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 50 },
    {
      field: 'region',
      headerName: 'Market',
      width: 200,
      editable: false,
    },
    {
      field: 'city',
      headerName: 'City',
      width: 120,
      editable: false,
    },
    {
      field: 'country',
      headerName: 'Country',
      width: 200,
      editable: false,
    },
    {
      field: 'started_at_dates',
      headerName: 'Start Date',
      width: 150,
      editable: false,
    },
    {
      field: 'ended_at_dates',
      headerName: 'End Date',
      width: 150,
      editable: false,
      renderCell: params => (
        <div style={{ color: new Date(params.value) < new Date() ? 'red' : '' }}>
          {params.value}
        </div>
      ),
    },
  ];

  const estimatorReportColumn: GridColDef[] = [
    { field: 'index', headerName: 'Index', width: 50 },
    { field: 'id', headerName: 'ID', width: 300 },
    {
      field: 'address_full',
      headerName: 'Address Full',
      width: 200,
      editable: false,
    },
    {
      field: 'address_short',
      headerName: 'Address Short',
      width: 200,
      editable: false,
    },
    {
      field: 'bedrooms',
      headerName: 'Bedrooms',
      width: 100,
      editable: false,
    },
    {
      field: 'report_type',
      headerName: 'Report Type',
      width: 100,
      editable: false,
    },
    {
      field: 'coordinates',
      headerName: 'Coordinates',
      width: 200,
      editable: false,
    },
    {
      field: 'user_report_access_type',
      headerName: 'User Report Access Type',
      width: 100,
      editable: false,
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 150,
      editable: false,
    },
    {
      field: 'report_url',
      headerName: 'Report Url',
      width: 600,
      renderCell: params => (
        <Link href={params.value} target="_blank">
          {params.value}
        </Link>
      ),
    },
  ];

  const searchedRegionColumn: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 90 },
    {
      field: 'district',
      headerName: 'District Name',
      width: 260,
      editable: false,
    },
  ];

  useEffect(() => {
    if (userDetails) {
      setShowSearchbar(false);
      setUserCurrentSubscriptionDetails(null);
      setUserCurrentSubscriptionDetailsError(null);
      setUserSubscriptionHistory(null);
      setUserSubscriptionHistoryError(null);
      // set insights region in subscribedInsightRegionRows
      let rows: any = [];
      userDetails.regions.map((data, index) => {
        let tempRows = {
          id: index + 1,
          region: data?.region,
          city: data?.city,
          country: data?.country,
          started_at_dates: data?.started_at_dates?.slice(-1)[0],
          ended_at_dates: data?.ended_at_dates?.slice(-1)[0],
        };
        rows.push(tempRows);
      });
      setSubscribedInsightRegionRows(rows);

      // set Insights searched region in setSearchedInsightRegionRows
      let InsightSearchRows: any = [];
      userDetails.searchedRegions.map((region, index) => {
        let tempRows = {
          id: index + 1,
          district: region,
        };
        InsightSearchRows.push(tempRows);
      });
      setSearchedInsightRegionRows(InsightSearchRows);
      // console.log(InsightSearchRows);

      // Set estimator reports
      let tmpEstimatorReportRows: any = [];
      userDetails['user_estimator_reports'].map((data, index) => {
        let tempRows = {
          index: index + 1,
          id: data?.id,
          address_full: data?.address_full,
          address_short: data?.address_short,
          bedrooms: data?.bedrooms,
          report_type: data?.report_type,
          coordinates: `${data?.latitude}, ${data?.longitude}`,
          user_report_access_type: data?.user_report_access_type,
          created_at: data?.created_at,
          report_url: window.location.origin + '/' + data?.url_path,
        };
        tmpEstimatorReportRows.push(tempRows);
      });
      setEstimatorReportRows(tmpEstimatorReportRows);

      const encodedEmail = encodeURIComponent(userEmailId);
      getUserSubscriptionDetails(
        encodedEmail,
        adminPassword,
        setUserCurrentSubscriptionDetails,
        setUserCurrentSubscriptionDetailsError,
        {
          returnAllSubscriptions: true,
        }
      );

      getUserSubscriptionHistory(
        encodedEmail,
        adminPassword,
        setUserSubscriptionHistory,
        setUserSubscriptionHistoryError
      );
    }
  }, [userDetails]);

  const fetchUserdata = async () => {
    setShowLoader(true);
    setShowUserNotFoundAlert(false);
    setShowNotAdminUserAlert(false);
    const url =
      'https://b9af2vkl1m.execute-api.us-east-2.amazonaws.com/default/getAirbticsUserDetails';
    const config = {
      queryParams: {
        admin_password: encodeURIComponent(adminPassword),
        search_user_email: encodeURIComponent(userEmailId),
      },
    };
    const res = await callApi(url, config, false);
    if (res.message == 'User not found in the database') {
      setShowLoader(false);
      setShowUserNotFoundAlert(true);
    } else if (res.message == 'Not a Admin User') {
      setShowLoader(false);
      setShowNotAdminUserAlert(true);
    } else {
      setUserDetails(res.message);
      setShowLoader(false);
    }
  };

  const convertTimestamp = (timestamp: number, timezone: string) => {
    const date = new Date(timestamp * 1000);

    const options: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: false,
    };

    const time = date.toLocaleString('en-US', options);

    // options.timeZone = 'UTC';
    // const utcTime = date.toLocaleString('en-US', options);

    return time;
  };

  const handleClearRateLimitsBtn = async () => {
    setClearRateLimitProcessing(true);
    const response = await clearRateLimits(userEmailId, adminPassword);
    console.log('Response clear rate limits: ', response);    
    setClearRateLimitProcessing(false);
    if (response) {
      alert(response?.message);
    }
  };

  const RemoveAppSubscriptionModal = () => {
    const [subscriptionRevokeConfirmText, setConfirmText] = useState('');
    const isConfirmValid = subscriptionRevokeConfirmText === 'confirm';

    const handleRemoveAppSubscription = async () => {
      if (subscriptionRevokeConfirmText !== 'confirm') {
        alert('Please type "confirm" to proceed');
        return;
      }
      setRevokeAppSubscriptionProcessing(true);
      const URL = "https://xy9qki0v66.execute-api.us-east-2.amazonaws.com/prod/revokeUserAppSubscription"
      const config = {
        body: {
            adminPassword: adminPassword,
            userEmail: userEmailId,
          },
      };
      const response = await callApi(URL, config, true, false, '', 'POST', true, undefined, true, true);
      console.log('Response revoke app subscription: ', response);
      if (response.message) {
        alert('App subscription revoked successfully');  
        fetchUserdata();
        setOpenRemoveAppAccessModal(false);
      } else {
        alert('Failed to revoke app subscription: ' + response.error);
        setOpenRemoveAppAccessModal(false);
      }
      setRevokeAppSubscriptionProcessing(false);
    };

    return (
      <Modal
        open={openRemoveAppAccessModal}
        onClose={() => {
          setOpenRemoveAppAccessModal(false);
          setConfirmText('');
        }}
      >
        <Box
          sx={{
            width: '35vw',
            height: 'auto',
            minHeight: '30vh',
            maxHeight: '70vh',
            background: '#fff',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            padding: '30px',
            borderRadius: 4,
          }}
        >
          {revokeAppSubscriptionProcessing && <LinearProgress sx={{ mb: '20px'}} />}
          <Typography variant="h5" style={{ textAlign: 'center', width: '100%' }}>
            Revoke App Subscription
          </Typography>
          <Typography sx={{ mt: 2, textAlign: 'center' }}>
            Are you sure you want to revoke app subscription for{' '}
            <Typography component="span" sx={{ color: COLOR_TERTIARY_1_500, fontWeight: 500 }}>
              {userEmailId}
            </Typography>
            ?
          </Typography>
          <Typography sx={{ mt: 2, textAlign: 'center', fontSize: '14px' }}>
            Type <strong>confirm</strong> to proceed
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Type confirm"
            value={subscriptionRevokeConfirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            sx={{ mt: 1 }}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              mt: 4,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setOpenRemoveAppAccessModal(false);
                setConfirmText('');
              }}
            >
              Cancel
            </Button>
            <Button
              sx={{ backgroundColor: 'red', color: 'white' }}
              disabled={!isConfirmValid}
              onClick={() => {
                handleRemoveAppSubscription();
              }}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  };

  const AddCreditModal = () => {
    const [emailID, setEmailID] = useState<string>(userEmailId);
    const [addCreditsResponse, setAddCreditsResponse] = useState<string>();
    const [showLoaderInAddCreditModal, setShowLoaderInAddCreditModal] = useState<boolean>(false);
    const [selectedPlan, setSelectedPlan] = useState<string>('expert');
    const [duration, setDuration] = useState<string>('month');

    const handleAddCreditBtn = async () => {
      setShowLoaderInAddCreditModal(true);
      setAddCreditsResponse('');
      const config = {
        queryParams: {
          email: encodeURIComponent(emailID),
          duration: duration,
          planName: selectedPlan,
        },
      };

      const res: any = await callApi(
        'https://7pbdt7zelg.execute-api.us-east-2.amazonaws.com/default/addUserCreditsToDynamoDB',
        config,
        true
      );
      setAddCreditsResponse(res.message);
      setShowLoaderInAddCreditModal(false);
      if (res.message.includes('Successfully')) {
        fetchUserdata();
        setOpenAddCreditsModal(false);
      }
    };
    

    return (
      <Modal
        open={openAddCreditsModal}
        onClose={() => {
          setOpenAddCreditsModal(!openAddCreditsModal);
        }}
      >
        <Box
          sx={{
            width: '50vw',
            height: '50vh',
            background: '#fff',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            padding: '20px',
            borderRadius: 4,
          }}
        >
          <Typography variant="h5" style={{ textAlign: 'center' }}>
            Add Credits To User DynamoDB
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              marginTop: '10px',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography style={{ width: '200px', display: 'flex', alignItems: 'center' }}>
                Email ID:
              </Typography>
              <TextField
                sx={{ width: '200px' }}
                id="outlined-basic"
                variant="outlined"
                value={emailID}
                onChange={e => {
                  setEmailID(e.target.value);
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography style={{ width: '200px', display: 'flex', alignItems: 'center' }}>
                Plan:
              </Typography>
              <FormControl>
                <Select
                  sx={{ width: '200px' }}
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={selectedPlan}
                  label=""
                  onChange={e => {
                    setSelectedPlan(e.target.value);
                  }}
                >
                  {/* <MenuItem value={'pro'}>Pro</MenuItem> */}
                  <MenuItem value={'expert'}>Expert</MenuItem>
                  <MenuItem value={'scale'}>Scale</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography style={{ width: '200px', display: 'flex', alignItems: 'center' }}>
                Duration:
              </Typography>
              <FormControl>
                <Select
                  sx={{ width: '200px' }}
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={duration}
                  label=""
                  onChange={e => {
                    setDuration(e.target.value);
                  }}
                >
                  <MenuItem value={'month'}>Month</MenuItem>
                  <MenuItem value={'year'}>Year</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Button
              style={{ width: '80%', backgroundColor: '#52C879', color: 'white' }}
              onClick={handleAddCreditBtn}
            >
              {showLoaderInAddCreditModal ? (
                <CircularProgress size={20} sx={{ color: SIDEBAR_COLOR }} />
              ) : (
                'Give Access'
              )}
            </Button>
            {addCreditsResponse && addCreditsResponse.includes('Successfully') && (
              <Alert severity="success" sx={{ width: '75%' }}>
                {addCreditsResponse}
              </Alert>
            )}

            {addCreditsResponse && !addCreditsResponse.includes('Successfully added') && (
              <Alert severity="error" sx={{ width: '75%' }}>
                {addCreditsResponse}
              </Alert>
            )}
          </Box>
        </Box>
      </Modal>
    );
  };

  const DeleteUserModal = () => {
    const [deletedBy, setDeletedBy] = useState<string>('');
    const [response, setResponse] = useState<string>();
    const [showLoaderInDeleteUserModal, setShowLoaderInDeleteUserModal] = useState<boolean>(false);

    const handleDeleteUserBtn = async () => {
      setShowLoaderInDeleteUserModal(true);
      setResponse('');
      const config = {
        queryParams: {
          deletedUser: encodeURIComponent(userEmailId),
          deletedBy: deletedBy,
        },
      };
      if (deletedBy) {
        const res: any = await callApi(
          'https://n4k7t590h1.execute-api.us-east-2.amazonaws.com/default/deleteUserFromDynamoDB',
          config,
          true
        );
        setResponse(res.message);
        setShowLoaderInDeleteUserModal(false);
      } else {
        setResponse('Enter you Name');
        setShowLoaderInDeleteUserModal(false);
      }
    };

    return (
      <Modal
        open={openDeleteUserModal}
        onClose={() => {
          setOpenDeleteUserModal(!openDeleteUserModal);
        }}
      >
        <Box
          sx={{
            width: '35vw',
            height: '35vh',
            background: '#fff',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            padding: '30px',
            borderRadius: 4,
          }}
        >
          <Typography variant="h5" style={{ textAlign: 'center', width: '100%' }}>
            Do you want to delete user{' '}
            <Typography variant="h5" sx={{ color: COLOR_TERTIARY_1_500 }}>
              {userEmailId}?
            </Typography>
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              marginTop: '10px',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography style={{ width: '200px', display: 'flex', alignItems: 'center' }}>
                Your Name:
              </Typography>
              <TextField
                id="outlined-basic"
                variant="outlined"
                value={deletedBy}
                onChange={e => {
                  setDeletedBy(e.target.value);
                }}
              />
            </Box>
            <Button
              style={{ width: '80%', backgroundColor: '#52C879', color: 'white' }}
              onClick={handleDeleteUserBtn}
            >
              Delete User
            </Button>
            {showLoaderInDeleteUserModal && <CircularProgress />}
            {response && response.includes('User Deleted Successfully') && (
              <Alert severity="success" sx={{ width: '75%' }}>
                {response}
              </Alert>
            )}

            {response && !response.includes('User Deleted Successfully') && (
              <Alert severity="error" sx={{ width: '75%' }}>
                {response}
              </Alert>
            )}
          </Box>
        </Box>
      </Modal>
    );
  };
  return (
    <Box sx={{ margin: '10px' }}>
      <AddCreditModal />
      <RemoveAppSubscriptionModal />
      <DeleteUserModal />
      <Grid spacing={2} container sx={{ width: '100%' }} alignItems={'flex-start'}>
        <Grid item xs={12}>
          {dataType == 'user' && (
            <Box>
              {showSearchbar && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TextField
                    id="outlined-basic"
                    label="email"
                    variant="outlined"
                    sx={{
                      backgroundColor: 'white',
                      borderRadius: '10px 0px 0px 10px',
                      width: '50%',
                      '&:focus': {
                        border: 'none',
                        outline: 'none',
                      },
                    }}
                    onChange={e => setUserEmailId(e.target.value)}
                  />
                  <Button
                    sx={{
                      backgroundColor: COLOR_SUCCESS_1,
                      color: 'white',
                      height: '56px',
                      width: '85px',
                      borderRadius: '0px 10px 10px 0px',
                    }}
                    onClick={fetchUserdata}
                  >
                    Search
                  </Button>
                </Box>
              )}
              {showLoader && (
                <Box sx={{ display: 'flex', justifyContent: 'center', paddingTop: '10px' }}>
                  <CircularProgress />
                </Box>
              )}

              {showUserNotFoundAlert && (
                <Alert severity="error" sx={{ marginTop: '20px' }}>
                  User not found in the database
                </Alert>
              )}

              {showNotAdminUserAlert && (
                <Alert severity="error" sx={{ marginTop: '20px' }}>
                  Your Admin Password Is Incorrect
                </Alert>
              )}

              {!showSearchbar && userDetails && (
                <Box sx={{ backgroundColor: '#B2BEB5', height: '620px' }}>
                  <Grid container>
                    <Grid item sm={12} md={12} lg={12} sx={{ height: '70px' }}>
                      <Box
                        sx={{ display: 'flex', justifyContent: 'space-between', margin: '10px' }}
                      >
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {userDetails?.userName}
                          </Typography>
                          <Typography variant="body1">{userDetails?.email}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                          {userDetails?.isDeleted && (
                            <SelecFilterChip
                              height="30px"
                              width="120px"
                              borderRadius="30px"
                              colors={{
                                backgroundColor: COLOR_ERROR_BACKGROUNG,
                                color: COLOR_ERROR,
                                borderColor: COLOR_ERROR_BACKGROUNG,
                              }}
                              text="Deleted"
                              fontWeight="bold"
                              handleOnClick={() => {}}
                              isSelected={false}
                              tooltip=""
                            />
                          )}
                          {userDetails?.isVerified && (
                            <SelecFilterChip
                              height="30px"
                              width="120px"
                              borderRadius="30px"
                              colors={{
                                backgroundColor: COLOR_SUCCESS_BACKGROUNG,
                                color: COLOR_SUCCESS_1,
                                borderColor: COLOR_SUCCESS_BACKGROUNG,
                              }}
                              text="Verified"
                              fontWeight="bold"
                              handleOnClick={() => {}}
                              isSelected={false}
                              tooltip=""
                            />
                          )}

                          <Button variant="contained" color="primary" onClick={handleClearRateLimitsBtn}>
                            Refresh Rate Limits
                            {clearRateLimitProcessing && <CircularProgress size={20} sx={{ color: 'white' }} />}
                          </Button>

                          {userDetails?.isSubscribed && (
                            <SelecFilterChip
                              height="30px"
                              width="120px"
                              borderRadius="30px"
                              colors={{
                                backgroundColor: COLOR_SUCCESS_BACKGROUNG,
                                color: COLOR_SUCCESS_1,
                                borderColor: COLOR_SUCCESS_BACKGROUNG,
                              }}
                              text="Subscribed"
                              fontWeight="bold"
                              handleOnClick={() => {}}
                              isSelected={false}
                              tooltip=""
                            />
                          )}
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sx={{ height: '520px', overflowY: 'scroll' }}>
                      <Box sx={{ border: '2px solid grey', padding: '5px', borderRadius: '10px' }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          Sign up time
                        </Typography>
                        <Typography variant="body1">
                          PHT {convertTimestamp(userDetails.signupTimeStamp, 'Asia/Manila')}
                        </Typography>
                        <Typography variant="body1">
                          UTC {convertTimestamp(userDetails.signupTimeStamp, 'UTC')}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          border: '2px solid grey',
                          padding: '5px',
                          borderRadius: '10px',
                          marginTop: '10px',
                        }}
                      >
                        <Typography variant="body1">
                          Stripe Account:{' '}
                          <span style={{ fontWeight: 500, color: '#000' }}>
                            {userDetails?.user_stripe_payment_account === 'airbtics_uk'
                              ? 'UK'
                              : userDetails?.user_stripe_payment_account === 'airbtics_us'
                              ? 'US'
                              : ''}
                          </span>
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          border: '2px solid grey',
                          padding: '5px',
                          borderRadius: '10px',
                          marginTop: '10px',
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          User Details
                        </Typography>
                        <Typography variant="body1">
                          Occupation: {userDetails?.user_description}
                        </Typography>
                        <Typography variant="body1">
                          No of Listings: {userDetails?.user_sub_description}
                        </Typography>
                        <Typography variant="body1">
                          Landing Page: {userDetails?.landing_page}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          border: '2px solid grey',
                          padding: '5px',
                          borderRadius: '10px',
                          marginTop: '10px',
                        }}
                      >
                        <Typography sx={{ fontWeight: 500 }}>Current Subscription</Typography>
                        {userCurrentSubscriptionDetailsError ? (
                          userCurrentSubscriptionDetailsError
                        ) : userCurrentSubscriptionDetails ? (
                          <>
                            <ActiveSubscriptionCard
                              setIsModalOpen={() => {}}
                              userSubscriptionDetails={userCurrentSubscriptionDetails[0]}
                              setShowUpgradeYearlySubscriptionModal={() => {}}
                              finalDueAmount={null}
                              adminDashboardView={true}
                            />
                            {userCurrentSubscriptionDetails.length > 1 && (
                              <>
                                <Typography variant="body1">
                                  Other subscription history shown to the user
                                </Typography>
                                {userCurrentSubscriptionDetails
                                  .slice(1)
                                  .map((subscription, index) => (
                                    <ActiveSubscriptionCard
                                      key={'card-sub-user-' + index}
                                      setIsModalOpen={() => {}}
                                      userSubscriptionDetails={subscription}
                                      setShowUpgradeYearlySubscriptionModal={() => {}}
                                      finalDueAmount={null}
                                      adminDashboardView={true}
                                      hideUpgradeButton={true}
                                    />
                                  ))}
                              </>
                            )}
                          </>
                        ) : (
                          <CircularProgress size={20} />
                        )}
                      </Box>

                      <Box
                        sx={{
                          border: '2px solid grey',
                          padding: '5px',
                          borderRadius: '10px',
                          marginTop: '10px',
                        }}
                      >
                        <Grid
                          container
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mb: '5px' }}
                        >
                          <Typography sx={{ fontWeight: 500 }}>Subscription History</Typography>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            size={'small'}
                            onClick={() => {
                              setUserSubscriptionHistory(null);
                              setUserSubscriptionHistoryError(null);
                              getUserSubscriptionHistory(
                                userDetails.email,
                                adminPassword,
                                setUserSubscriptionHistory,
                                setUserSubscriptionHistoryError,
                                true
                              );
                            }}
                          >
                            Refresh
                          </Button>
                        </Grid>

                        {userSubscriptionHistoryError ? (
                          userSubscriptionHistoryError
                        ) : userSubscriptionHistory ? (
                          <UserSubscriptionHistory
                            userDetails={userDetails}
                            userSubscriptionHistory={userSubscriptionHistory}
                          />
                        ) : (
                          <CircularProgress size={20} />
                        )}
                      </Box>

                      <Box
                        sx={{
                          border: '2px solid grey',
                          padding: '5px',
                          borderRadius: '10px',
                          marginTop: '10px',
                        }}
                      >
                        <Typography sx={{ fontWeight: 500 }}>Credits</Typography>
                        <Typography variant="body1">
                          Insights Credits: {userDetails.credits}
                        </Typography>
                        <Typography variant="body1">
                          Estimator Credits: {userDetails.estimatorCredits}
                        </Typography>
                        <Typography variant="body1">
                          Subscription Plan:{' '}
                          {userDetails?.subscription_plan
                            ? userDetails?.subscription_plan
                            : 'Not Subscribed'}
                        </Typography>
                        <Typography variant="body1">
                          Subscription Date:{' '}
                          {userDetails?.subscription_date ? userDetails?.subscription_date : 'N/A'}
                        </Typography>
                        <Button
                          style={{
                            backgroundColor: '#52C879',
                            color: 'white',
                            width: '100%',
                          }}
                          onClick={() => {
                            setOpenAddCreditsModal(!openAddCreditsModal);
                          }}
                        >
                          <Typography style={{ padding: '2px' }}>Give App Access</Typography>
                        </Button>
                        <Button
                          style={{
                            backgroundColor: '#f44336',
                            color: 'white',
                            width: '100%',
                            marginTop: '8px',
                          }}
                          disabled={userDetails.subscription_plan === "starter"}
                          onClick={() => {
                            setOpenRemoveAppAccessModal(true);
                          }}
                        >
                          <Typography style={{ padding: '2px' }}>Revoke App Subscription</Typography>
                        </Button>
                      </Box>
                      <Box
                        sx={{
                          border: '2px solid grey',
                          padding: '5px',
                          borderRadius: '10px',
                          marginTop: '10px',
                        }}
                      >
                        <Typography sx={{ fontWeight: 500 }}>Subscribed Countries</Typography>
                        <Box sx={{ display: 'flex' }}>
                          <Typography sx={{ width: '150px' }}>Country Code</Typography>
                          <Typography sx={{ width: '150px' }}>Starting Date</Typography>
                          <Typography sx={{ width: '150px' }}>Ending Date</Typography>
                        </Box>
                        {userDetails.country_wide_access_list.map((country, index) => (
                          <Box key={index} sx={{ display: 'flex' }}>
                            <Typography sx={{ width: '150px' }}>{country.country_code}</Typography>
                            <Typography sx={{ width: '150px' }}>
                              {country.started_at_date.slice(-1)[0]}
                            </Typography>
                            <Typography
                              sx={{
                                width: '150px',
                                color:
                                  new Date(country.ended_at_date.slice(-1)[0]) < new Date()
                                    ? 'red'
                                    : '',
                              }}
                            >
                              {country.ended_at_date.slice(-1)[0]}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      <Button
                        sx={{
                          backgroundColor: 'red',
                          color: 'black',
                          width: '100%',
                          marginTop: '10px',
                        }}
                        onClick={() => {
                          setOpenDeleteUserModal(true);
                        }}
                      >
                        DELETE
                      </Button>
                    </Grid>
                    <Grid item xs={6} sx={{ height: '500px' }}>
                      <Box
                        sx={{
                          border: '2px solid grey',
                          marginLeft: '10px',
                          borderRadius: '10px',
                        }}
                      >
                        <Box
                          sx={{
                            padding: '5px',
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Subscribed Markets ({selectedDashboard.toUpperCase()})
                          </Typography>
                          <Box sx={{ display: 'flex' }}>
                            <Button
                              sx={{
                                backgroundColor: COLOR_GREY_200,
                                border: '1px solid grey',
                                color: 'black',
                              }}
                              onClick={() => {
                                setSelectedDashboard('insights');
                              }}
                            >
                              Insights
                            </Button>
                            <Button
                              sx={{
                                backgroundColor: COLOR_GREY_200,
                                border: '1px solid grey',
                                color: 'black',
                              }}
                              onClick={() => {
                                setSelectedDashboard('estimator');
                              }}
                            >
                              Estimator
                            </Button>
                          </Box>
                        </Box>
                        <Box sx={{ height: 250, width: '100%' }}>
                          {selectedDashboard == 'insights' ? (
                            <DataGrid
                              rows={subscribedInsightRegionRows}
                              columns={subscribedInsightsRegionColumn}
                              hideFooter={true}
                            />
                          ) : (
                            <DataGrid
                              rows={estimatorReportRows}
                              columns={estimatorReportColumn}
                              hideFooter={true}
                            />
                          )}
                        </Box>
                      </Box>
                      {/* For searched markets */}
                      <Box
                        sx={{
                          border: '2px solid grey',
                          marginLeft: '10px',
                          borderRadius: '10px',
                          marginTop: '5px',
                        }}
                      >
                        <Box
                          sx={{
                            padding: '5px',
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Searched Markets (INSIGHTS)
                          </Typography>
                        </Box>
                        <Box sx={{ height: 190, width: '100%' }}>
                          <DataGrid
                            rows={searchedInsightsRegionRows}
                            columns={searchedRegionColumn}
                            hideFooter={true}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

const UserSubscriptionHistory = ({ userDetails, userSubscriptionHistory }) => {
  useEffect(() => {
    console.log(userDetails);
  }, [userDetails]);

  if (
    !userSubscriptionHistory ||
    !userSubscriptionHistory.message ||
    userSubscriptionHistory.length === 0
  ) {
    return (
      <Typography variant="body2" color="text.secondary" textAlign="center">
        No subscription history available.
      </Typography>
    );
  }

  const checkIfCurrentPlanIsModifiedByCheckingSubscriptionHistory = (sub, index, planInfo) => {
    if (index > 0) {
      return {
        status: false,
        modifiedPlan: null,
        modifiedPlanFrequency: null,
        source: 'Stripe Subscription History Check',
      };
    }

    const isCurrentPlanTypeModified =
      index === 0 &&
      getPlanLabelFromPriceID(sub.plan_name).planKey !== userDetails.subscription_plan;
    const isCurrentPlanFrequencyChanged =
      index === 0 &&
      sub.duration !== getPlanDurationFrequency(userDetails.active_subscription_details);

    const status = isCurrentPlanTypeModified || isCurrentPlanFrequencyChanged;

    if (status) {
      return {
        status,
        modifiedPlan: userDetails.subscription_plan,
        modifiedPlanFrequency: getPlanDurationFrequency(userDetails.active_subscription_details),
        source: 'Stripe Subscription History Check',
      };
    }

    return {
      status: false,
      modifiedPlan: null,
      modifiedPlanFrequency: null,
      source: 'Stripe Subscription History Check',
    };
  };

  const checkIfCurrentPlanIsModifiedByTrackedChangesFromDynamoDB = (sub, index) => {
    const manualPlanModificationHistoryArray = userDetails.manual_plan_modification_history;

    if (manualPlanModificationHistoryArray.length === 0) {
      return {
        status: false,
        modifiedPlan: null,
        modifiedPlanFrequency: null,
        source: 'Tracked Modifications',
      };
    }

    for (const modification of manualPlanModificationHistoryArray) {
      if (modification.subscription_id === sub.subscription_id) {
        return {
          status: true,
          modifiedPlan: modification.plan_name,
          modifiedPlanFrequency: modification.duration,
          source: 'Tracked Modifications',
        };
      }
    }

    return {
      status: false,
      modifiedPlan: null,
      modifiedPlanFrequency: null,
      source: 'Tracked Modifications',
    };
  };

  const checkIfCurrentPlanIsModified = (sub, index, planInfo) => {
    const trackedChanges = checkIfCurrentPlanIsModifiedByTrackedChangesFromDynamoDB(sub, index);
    const subscriptionHistory = checkIfCurrentPlanIsModifiedByCheckingSubscriptionHistory(
      sub,
      index,
      planInfo
    );

    return trackedChanges.status ? trackedChanges : subscriptionHistory;
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {userSubscriptionHistory.message.map((sub, index) => {
        const planInfo = getPlanLabelFromPriceID(sub.plan_name);

        const {
          status: isCurrentPlanModified,
          modifiedPlan,
          modifiedPlanFrequency,
          source,
        } = checkIfCurrentPlanIsModified(sub, index, planInfo);

        return (
          <Card key={sub.subscription_id} variant="outlined" sx={{ backgroundColor: '#f5f5f5' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1" fontWeight="bold">
                  {planInfo.text}
                </Typography>
                <Stack direction="row" spacing={1}>
                  {sub.status && (
                    <Chip
                      label={sub.status}
                      color={sub.status === 'active' ? 'success' : 'error'}
                      size="small"
                    />
                  )}
                  {isCurrentPlanModified && <Chip label="Modified" color="warning" size="small" />}
                </Stack>
              </Box>

              <Typography variant="body2" color="text.secondary" mt={1}>
                {`${sub.price} ${sub.currency} / ${sub.duration}`}
              </Typography>
              {isCurrentPlanModified && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Modified Plan:{' '}
                  <span style={{ fontWeight: 'bold' }}>
                    {capitalize(modifiedPlan)} -{' '}
                    {modifiedPlanFrequency ? capitalize(modifiedPlanFrequency) + 'ly' : 'N/A'}
                    <Typography variant="caption" color="text.secondary" mt={2} display="block">
                      Source: {source}
                    </Typography>
                  </span>
                </Alert>
              )}

              <Typography variant="body2" mt={1}>
                Start Date: {formatDate(sub.start_date)}
              </Typography>
              <Typography variant="body2">End Date: {formatDate(sub.end_date)}</Typography>

              <Typography variant="caption" color="text.secondary" mt={2} display="block">
                Subscription ID: {sub.subscription_id}
              </Typography>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};

export default User;
